import React from 'react';
import { submitMyW9Document, uploadMyW9 } from '../../services/vimPay/contractorService.js';
import {
  generateSubstituteW9Pdf,
  validateW9Builder,
  VIM_PAY_W9_FORM_REVISION,
  VIM_PAY_W9_INSTRUCTIONS_URL,
  VIM_PAY_W9_IRS_URL,
} from '../../services/vimPay/w9PdfService.js';

function currentW9(documents = []) {
  return documents.find((document) => document.document_type === 'w9' && document.is_current !== false)
    || documents.find((document) => document.document_type === 'w9')
    || null;
}

function sourceLabel(document) {
  if (!document) return 'No W-9 submitted yet';
  return document.submission_source === 'vim_pay_generated'
    ? `Generated in VIM Pay${document.form_revision ? ` • Rev. ${document.form_revision}` : ''}`
    : 'Uploaded signed W-9';
}

export default function VimPayW9Builder({ userId, profile, taxDocuments = [], onSubmitted }) {
  const [values, setValues] = React.useState({
    federal_tax_classification: 'individual',
    tin_type: 'ssn_itin',
    us_person_confirmed: false,
    certification_accepted: false,
    subject_to_backup_withholding: false,
    has_foreign_partners: false,
  });
  const [tin, setTin] = React.useState('');
  const [signatureName, setSignatureName] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [manualFile, setManualFile] = React.useState(null);
  const [showManual, setShowManual] = React.useState(false);

  React.useEffect(() => {
    if (!profile?.id) return;
    const profileAddress = [profile.address_line1, profile.address_line2].filter(Boolean).join(', ');
    const profileCityStateZip = [profile.city, profile.state_region, profile.postal_code].filter(Boolean).join(', ');
    setValues((current) => ({
      ...current,
      name_line1: current.name_line1 || profile.legal_name || '',
      address_line1: current.address_line1 || profileAddress,
      city_state_zip: current.city_state_zip || profileCityStateZip,
    }));
    setSignatureName((current) => current || profile.legal_name || '');
  }, [profile?.id, profile?.legal_name, profile?.address_line1, profile?.address_line2, profile?.city, profile?.state_region, profile?.postal_code]);

  const latest = currentW9(taxDocuments);
  const profileReadyForW9 = Boolean(
    String(profile?.legal_name || '').trim()
    && String(profile?.address_line1 || '').trim()
    && String(profile?.city || '').trim()
    && String(profile?.state_region || '').trim()
    && String(profile?.postal_code || '').trim()
  );
  const flowThrough = ['partnership', 'trust_estate'].includes(values.federal_tax_classification)
    || (values.federal_tax_classification === 'llc' && values.llc_tax_classification === 'P');

  function field(name, value) {
    setValues((current) => ({ ...current, [name]: value }));
  }

  async function submitGenerated(event) {
    event.preventDefault();
    setError('');
    setMessage('');
    const validation = validateW9Builder(values, tin, signatureName);
    if (validation.length) {
      setError(validation[0]);
      return;
    }

    setBusy(true);
    try {
      const certifiedAt = new Date().toISOString();
      const blob = await generateSubstituteW9Pdf({ values, tin, signatureName, certifiedAt });
      const file = new File([blob], `VIM-Pay-W9-${VIM_PAY_W9_FORM_REVISION}.pdf`, { type: 'application/pdf', lastModified: Date.now() });
      await submitMyW9Document({
        userId,
        contractorId: profile.id,
        file,
        source: 'vim_pay_generated',
        formRevision: VIM_PAY_W9_FORM_REVISION,
        certificationAccepted: true,
        certifiedByName: signatureName,
      });

      // Sensitive TIN is intentionally transient. Clear it immediately after the PDF is registered.
      setTin('');
      setSignatureName('');
      setValues((current) => ({ ...current, certification_accepted: false }));
      setMessage('Your signed W-9 PDF was generated securely and submitted to VIM Media for review.');
      await onSubmitted?.();
    } catch (err) {
      setError(err?.message || 'Unable to generate and submit your W-9.');
    } finally {
      setBusy(false);
    }
  }

  async function submitManual(event) {
    event.preventDefault();
    if (!manualFile) return;
    setBusy(true);
    setError('');
    setMessage('');
    try {
      await uploadMyW9({ userId, contractorId: profile.id, file: manualFile });
      setManualFile(null);
      setMessage('Your signed W-9 was uploaded securely and submitted to VIM Media for review.');
      await onSubmitted?.();
    } catch (err) {
      setError(err?.message || 'Unable to upload your W-9.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="vim-pay-card vim-pay-onboarding-form">
      <div className="vim-pay-form-section">
        <span className="vim-pay-kicker">INDEPENDENT CONTRACTOR TAX STEP</span>
        <h2>Complete your W-9 securely</h2>
        <p>VIM Pay can generate your signed substitute Form W-9 (Rev. March 2024) from the information you enter here. Your TIN is used only in browser memory to create the PDF and is not saved in a normal VIM Pay database field.</p>
        <p><a href={VIM_PAY_W9_IRS_URL} target="_blank" rel="noreferrer">View the current IRS Form W-9</a> · <a href={VIM_PAY_W9_INSTRUCTIONS_URL} target="_blank" rel="noreferrer">IRS W-9 instructions</a></p>
      </div>

      <div className="vim-pay-tax-status">
        <span className={`vim-pay-badge ${profile.tax_status === 'verified' ? 'green' : 'gold'}`}>{String(profile.tax_status || 'not_started').replace(/_/g, ' ').toUpperCase()}</span>
        <span>{sourceLabel(latest)}</span>
      </div>

      {!profileReadyForW9 ? (
        <div className="vim-pay-safe-note"><strong>Finish your profile first</strong><p>Save your legal name and mailing address in the Worker Setup form above. This W-9 will automatically refresh with that information after you save it.</p></div>
      ) : null}

      <form onSubmit={submitGenerated} autoComplete="off">
        <div className="vim-pay-form-grid">
          <label className="span-2">1. Name of entity/individual
            <input value={values.name_line1 || ''} onChange={(event) => field('name_line1', event.target.value)} required />
          </label>
          <label className="span-2">2. Business/disregarded entity name, if different
            <input value={values.business_name || ''} onChange={(event) => field('business_name', event.target.value)} />
          </label>
          <label>3a. Federal tax classification
            <select value={values.federal_tax_classification || ''} onChange={(event) => field('federal_tax_classification', event.target.value)} required>
              <option value="individual">Individual / sole proprietor</option>
              <option value="c_corporation">C corporation</option>
              <option value="s_corporation">S corporation</option>
              <option value="partnership">Partnership</option>
              <option value="trust_estate">Trust / estate</option>
              <option value="llc">LLC</option>
              <option value="other">Other</option>
            </select>
          </label>
          {values.federal_tax_classification === 'llc' ? (
            <label>LLC tax classification
              <select value={values.llc_tax_classification || ''} onChange={(event) => field('llc_tax_classification', event.target.value)} required>
                <option value="">Select C, S, or P</option><option value="C">C corporation</option><option value="S">S corporation</option><option value="P">Partnership</option>
              </select>
            </label>
          ) : null}
          {values.federal_tax_classification === 'other' ? (
            <label>Other classification<input value={values.other_classification || ''} onChange={(event) => field('other_classification', event.target.value)} required /></label>
          ) : null}
          {flowThrough ? (
            <label className="span-2"><input type="checkbox" checked={Boolean(values.has_foreign_partners)} onChange={(event) => field('has_foreign_partners', event.target.checked)} /> 3b. This entity has foreign partners, owners, or beneficiaries where line 3b applies.</label>
          ) : null}
          <label>Exempt payee code, if any<input value={values.exempt_payee_code || ''} onChange={(event) => field('exempt_payee_code', event.target.value)} /></label>
          <label>FATCA exemption code, if any<input value={values.fatca_exemption_code || ''} onChange={(event) => field('fatca_exemption_code', event.target.value)} /></label>
          <label className="span-2">5. Address<input value={values.address_line1 || ''} onChange={(event) => field('address_line1', event.target.value)} required /></label>
          <label className="span-2">6. City, state, and ZIP<input value={values.city_state_zip || ''} onChange={(event) => field('city_state_zip', event.target.value)} required /></label>
          <label className="span-2">7. Account number(s), optional<input value={values.account_numbers || ''} onChange={(event) => field('account_numbers', event.target.value)} /></label>
        </div>

        <div className="vim-pay-form-section">
          <span className="vim-pay-kicker">PART I — TAXPAYER IDENTIFICATION NUMBER</span>
          <h3>TIN</h3>
          <p>This value is not written to a VIM Pay database field. It is placed only into the generated signed PDF.</p>
        </div>
        <div className="vim-pay-form-grid">
          <label>TIN type
            <select value={values.tin_type || 'ssn_itin'} onChange={(event) => field('tin_type', event.target.value)}>
              <option value="ssn_itin">SSN / ITIN</option><option value="ein">EIN</option>
            </select>
          </label>
          <label>{values.tin_type === 'ein' ? 'Employer identification number' : 'Social Security number / ITIN'}
            <input type="password" inputMode="numeric" autoComplete="off" maxLength="11" value={tin} onChange={(event) => setTin(event.target.value.replace(/[^0-9-]/g, ''))} required />
          </label>
        </div>
        <div className="vim-pay-safe-note"><strong>Private TIN handling</strong><p>VIM Pay does not store this number in your profile, localStorage, analytics, or a normal application table. After successful PDF submission, this field is cleared.</p></div>

        <div className="vim-pay-form-section">
          <span className="vim-pay-kicker">PART II — CERTIFICATION</span>
          <h3>Electronic signature</h3>
          <p>Your electronic signature is the final entry in this submission and certifies the W-9 under penalties of perjury.</p>
        </div>
        <div className="vim-pay-form-grid">
          <label className="span-2"><input type="checkbox" checked={Boolean(values.us_person_confirmed)} onChange={(event) => field('us_person_confirmed', event.target.checked)} /> I certify that the payee is a U.S. citizen or other U.S. person eligible to provide Form W-9.</label>
          <label className="span-2"><input type="checkbox" checked={Boolean(values.subject_to_backup_withholding)} onChange={(event) => field('subject_to_backup_withholding', event.target.checked)} /> The IRS has notified me that I am currently subject to backup withholding for failure to report interest or dividends.</label>
          {values.subject_to_backup_withholding ? <div className="vim-pay-safe-note span-2"><strong>Manual W-9 required</strong><p>IRS instructions require certification item 2 to be crossed out in this situation. Use the signed manual-upload option below.</p></div> : null}
          <label className="span-2">Type your electronic signature name<input value={signatureName} onChange={(event) => setSignatureName(event.target.value)} required /></label>
          <label className="span-2"><input type="checkbox" checked={Boolean(values.certification_accepted)} onChange={(event) => field('certification_accepted', event.target.checked)} /> I am the payee or authorized signer, and I electronically sign and certify this Form W-9 under penalties of perjury.</label>
        </div>

        {message ? <div className="vim-pay-success" role="status">{message}</div> : null}
        {error ? <div className="vim-pay-error" role="alert">{error}</div> : null}
        <div className="vim-pay-actions"><button className="vim-pay-primary" type="submit" disabled={busy || !profileReadyForW9 || values.subject_to_backup_withholding}>{busy ? 'Generating securely…' : profileReadyForW9 ? 'Generate, sign & submit W-9' : 'Save profile before W-9'}</button></div>
      </form>

      <div className="vim-pay-form-section">
        <span className="vim-pay-kicker">ALREADY HAVE A SIGNED W-9?</span>
        <button className="vim-pay-secondary" type="button" onClick={() => setShowManual((current) => !current)}>{showManual ? 'Hide manual upload' : 'Upload a signed W-9 instead'}</button>
      </div>
      {showManual ? (
        <form onSubmit={submitManual}>
          <p>Upload a completed, signed W-9 as PDF, JPG, or PNG. This remains available for special tax situations or workers who prefer the IRS paper form.</p>
          <label className="vim-pay-file-field">Signed W-9 document<input type="file" accept="application/pdf,image/jpeg,image/png" onChange={(event) => setManualFile(event.target.files?.[0] || null)} /></label>
          <div className="vim-pay-actions"><button className="vim-pay-secondary" type="submit" disabled={!manualFile || busy}>{busy ? 'Uploading securely…' : 'Submit signed W-9'}</button></div>
        </form>
      ) : null}
    </section>
  );
}
